import { memo } from 'react';
import { useTheme } from 'styled-components';

import { useOtcCounterpartiesQueryData } from '@src/providers/Counterparties/useOtcCounterpartiesQueryData';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import type { DealForwardData } from '@src/types/OTC';
import { formatOtcDealDateTimeInTimezone } from '@src/utils/OTC/otcDealDisplayUtils';
import { Divider, HStack, InlineFormattedNumber, SideEnum, Stack, Text, VStack } from '@talos/kyoko';
import { Wrapper } from '../OMSCard/styles';
import { DealCardHeader } from './DealCardHeader';
import { DealCardRow } from './DealCardRow';
import { useForwardNotionalCurrencies } from './useForwardNotionalCurrencies';
import { useOtcDealSettlementTimezone } from './useOtcDealSettlementTimezone';

interface DealCardForwardProps {
  deal: DealForwardData;
}

export const DealCardForward = memo(function DealCardForward({ deal }: DealCardForwardProps) {
  const theme = useTheme();
  const { subAccountsByName } = useSubAccounts();
  const { otcCounterpartiesByName } = useOtcCounterpartiesQueryData();
  const settlementTimezone = useOtcDealSettlementTimezone(deal);

  // Use the hook to get proper currencies
  const { forwardPriceCurrency } = useForwardNotionalCurrencies(deal);
  const settleTime = deal.DealInfo?.SettleTime;

  return (
    <Wrapper>
      <Stack gap="spacing8" alignItems="stretch" p="spacing4">
        {/* Header */}
        <DealCardHeader deal={deal} />

        <Divider color={theme.colors.gray['000']} />

        {/* Deal Details */}
        <VStack gap="spacing4" alignItems="stretch">
          <HStack justifyContent="space-between" alignItems="flex-start">
            <Text size="fontSizeSm" weight="fontWeightMedium" color={getSideColor(deal.DealInfo.Side)}>
              {deal.DealInfo.Side}
            </Text>
          </HStack>
          <Stack gap="spacing2">
            <HStack justifyContent="space-between">
              <Text size="fontSizeSmall">Notional</Text>
              <InlineFormattedNumber number={deal.DealInfo.NotionalAmount} currency={deal.DealInfo.NotionalAsset} />
            </HStack>
            <HStack justifyContent="space-between">
              <Text size="fontSizeSmall">Forward Price</Text>
              <InlineFormattedNumber number={deal.DealInfo.ForwardPrice} currency={forwardPriceCurrency} />
            </HStack>
            <HStack justifyContent="space-between">
              <Text size="fontSizeSmall">Settlement</Text>
              <Text size="fontSizeSmall" color="colorTextSecondary">
                {formatOtcDealDateTimeInTimezone(settleTime, settlementTimezone)}
              </Text>
            </HStack>
          </Stack>

          <DealCardRow label="Counterparty">
            {deal.Counterparty
              ? (otcCounterpartiesByName?.get(deal.Counterparty)?.DisplayName ?? deal.Counterparty)
              : deal.Counterparty}
          </DealCardRow>

          <DealCardRow label="Sub Account">
            {deal.SubAccount
              ? (subAccountsByName.get(deal.SubAccount)?.DisplayName ?? deal.SubAccount)
              : deal.SubAccount}
          </DealCardRow>
        </VStack>
      </Stack>
    </Wrapper>
  );
});

function getSideColor(side?: SideEnum) {
  return side === SideEnum.Buy ? 'colorTextPositive' : 'colorTextNegative';
}
